import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Chapter, Prose, TheoryBox, MTQuote } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";

export default function Beatles7() {
  const { lesson, course, accent } = findLesson("the-beatles", "7");
  return (
    <LessonShell
      course={course}
      courseSlug="the-beatles"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <Chapter eyebrow="Part 3 — vocal harmony" title="Singing is now the key">
        <Prose>
          <p>
            Parts 1 and 2 of "Because" covered the arpeggio, the chord progression, and the melody
            in the right hand. Part 3 moves into territory that surprises most piano students:
            <strong> vocal harmony</strong>. The Beatles wrote three-part harmony for "Because,"
            and understanding how those three voices work is, as Jason puts it, the whole point
            of this class.
          </p>
          <p>
            The song is in C♯ minor, in 4/4; the class scores it at 81 BPM. The chord roadmap Jason
            establishes is: C♯ minor → D♯ minor 7 flat 5 → G♯ major (he calls it A♭ major — the
            same keys) → A13 no 5 → D → D diminished 7. The voices enter on D major and
            D diminished 7. Every vocal note in the harmony is a chord tone of whichever chord is
            currently sounding.
          </p>
          <p>
            The assignment for the week is to play the piano part and sing each vocal layer
            separately. Not at the same time — one layer per attempt. "Your challenge now has to
            be to play this piano and sing these layers: high, melody, and baritone." This is how
            vocal harmony is learned: you isolate each part until it's in your ear and your voice,
            then eventually combine them.
          </p>
        </Prose>

        <TheoryBox label="Chord roadmap — Because (C♯ minor, 81 BPM)">
          Where the voices enter: <strong>D major</strong> →{" "}
          <strong>D diminished 7</strong>. Then the verse: <strong>C♯ minor</strong> →{" "}
          <strong>D♯ minor 7♭5</strong> → <strong>G♯ major</strong> (written A♭ on Jason's
          chart) → <strong>A13 no 5</strong> (the 5th is omitted) → back to D. "No means no.
          You just don't play the five." Every vocal note in the harmony will
          be a tone of the chord sounding at that moment — "you observe these are all chord
          tones from the respective chords."
        </TheoryBox>
      </Chapter>

      <YouTubeEmbed
        videoId="hL0tnrl2L_U"
        title="Because — The Beatles"
        caption="Listen for the three-part harmony throughout. The soprano (highest) is easiest to follow. The tenor carries the melody in a lower register. The baritone (lowest) holds the root area. Part 3 focuses entirely on learning to hear, notate, and sing each layer."
      />

      <Chapter eyebrow="Three-part harmony" title="Soprano, tenor, and baritone">
        <Prose>
          <p>
            There are three distinct vocal parts in "Because." Jason uses MuseScore to score them
            out: a harpsichord stave plus soprano, tenor, and baritone vocal staves. Setting up the
            project: C♯ minor, 4/4, 81 BPM. The harpsichord handles the arpeggio accompaniment
            (from Parts 1 and 2). The three vocal staves hold the harmony.
          </p>
          <p>
            The <strong>soprano</strong> is the highest part — the one most listeners instinctively
            follow. On the opening phrase, the soprano goes from G♯ to E. It sits above the
            melody.
          </p>
          <p>
            The <strong>tenor</strong> carries the actual melody — but because the Beatles are all
            male singers, this voice sits lower than you might expect. In MuseScore, Jason notates
            it on a "treble minus eight" clef — a treble clef with an 8 below it, meaning the notes
            sound an octave lower than written. "The melody is in the tenor register because a male
            human being is singing it."
          </p>
          <p>
            The <strong>baritone</strong> is the lowest voice. On the opening phrase, the baritone
            holds a very low C♯ — the root of the C♯ minor chord. "Almost like a different
            instrument, isn't it?" Once you hear it isolated, it sounds completely different from
            the melody line.
          </p>
        </Prose>

        <RomanNumeralAnalysis
          label="Because — opening chord progression (C♯ minor)"
          caption="D major and D diminished 7 are where the voices enter; C♯ minor is home. The D♯m7♭5 and G♯ major (Jason's chart writes A♭) come straight from C♯ harmonic minor. The chords from outside the key are the A13 (its G natural) and the D major / D diminished 7 it leads to."
          chords={[
            { chord: "D", rn: "♭II", function: "Borrowed" },
            { chord: "Ddim7", rn: "♭ii°7", function: "Borrowed" },
            { chord: "C#m", rn: "i", function: "Tonic" },
            { chord: "D#m7b5", rn: "iiø7", function: "Pre-dominant" },
            { chord: "G#", rn: "V", function: "Dominant" },
            { chord: "A13(no5)", rn: "V13/♭II", function: "Secondary V" },
          ]}
        />

        <MTQuote>
          "Singing is the key now. Don't think that this is a piano class. You have to sing
          those parts. That's the key — because of vocal harmony. So I would like everyone to
          submit some kind of homework where you're doing the harmony of Because by the Beatles."
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="Notating the harmony" title="How to map each voice in MuseScore">
        <Prose>
          <p>
            Jason scores the harmony in real time during the class, working out each voice by ear
            and entering it into MuseScore as the students listen. The process demonstrates how
            vocal harmony transcription actually works: you don't get all three parts at once. You
            isolate one voice at a time.
          </p>
          <p>
            Start with the soprano — it's the highest and most audible. On the opening phrase,
            the high part goes from G♯ to E. Once that's entered, move to the baritone.
            On the same phrase, the lowest voice holds a very low C♯. Finally, the middle
            (tenor/melody) becomes clear once the other two are accounted for.
          </p>
          <p>
            The key insight: "You just hold the chord and try to sing each note in sets. Now what
            might be the easiest thing for you to sing? Probably the top notes, isn't it?"
            Play the chord on the piano, hold it, and match each vocal part to the sounding notes.
            The voices will be chord tones — you're looking for which chord tone each singer
            takes at each moment.
          </p>
          <p>
            On the second phrase, one voice holds the same note — D♯ in C♯ minor, which Jason
            calls E♭ — and "stays on E flat for eternity," while the other voices move
            around it. Sustained notes against moving ones, in different rhythms, are a big part of
            the song's hypnotic quality.
          </p>
        </Prose>

        <NoteImage
          src="/notes/the-beatles/finals/07-because.png"
          caption="Because Pt3 — MuseScore layout: harpsichord arpeggio on the top stave, then soprano, tenor (treble-8 clef), and baritone. The first line is mapped with each vocal layer annotated at its pitch level."
        />

        <TheoryBox label="A YouTube resource for Beatles vocal harmony">
          Jason recommends a YouTube channel dedicated specifically to Beatles vocal harmony — the
          channel is "The Beatles Vocal Harmony," and its creator, the Italian musician Galeazzo Frudua, isolates
          and demonstrates each individual part for many Beatles songs. "I would recommend that
          you watch this video, how to sing Because. I was watching this last week. This guy's
          channel is incredible, you should definitely subscribe and watch his stuff. See, he does
          vocal harmony of all these songs. Basically his channel is literally called the Beatles
          vocal harmony." Watching how someone
          else hears and separates the parts is one of the fastest ways to train your ear for
          multi-voice music.
        </TheoryBox>
      </Chapter>
    </LessonShell>
  );
}
